import React from "react";
import { Box, Typography } from "@mui/material";
import logo from "./Logo.png";
import { API_BASE_URL } from "../../utils/api"; 

const formatDate = (d) =>
  new Date(d || Date.now()).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

// 🔹 Format address so City/State goes on a new line



const ViolationLetterTemplate = ({
  type,
  member = {},
  violation = {},
  date,
  content = "",
  formattedAddress,
  deliveryMethod 
}) => {
  const today = formatDate(date);

  // 🔹 Placeholders
  const tags = {
    date: today,
    name: `${member?.FirstName || ""} ${member?.LastName || ""}`,
    address: formattedAddress || member?.FullAddressHtml || "",
    email: member?.EmailAddress || "",
    property_address: member?.FullAddress  || "",
    violation_type: violation?.type || "",
    violation_rule: violation?.rule || "",
    required_action: violation?.required_action || "",
    days_to_cure: violation?.days_to_cure ?? "N/A",
    cure_by: violation?.cure_by ? formatDate(violation.cure_by) : "",
    description: violation?.description || "",
    id: violation?.id || "",
    property_id: violation?.property_id || "",
    status: violation?.status || "",
    resolution: violation?.resolution || "",
    violation_article: violation?.violation_article || "",
  };

  // 🔹 Replace placeholders
  // In ViolationLetterTemplate
const applyTags = (html) => {
  if (!html) return "";
  let output = html;
  Object.entries(tags).forEach(([key, value]) => {
    if (typeof value === "string") {
      // 🔹 ensure <br/> remains real HTML
      const safeValue = value.replace(/\n/g, "<br/>");
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "gi");
      output = output.replace(regex, safeValue);
    }
  });
  return output;
};


  // 🔹 Allow manual page breaks in template with <hr class="page-break" />
  const splitContent = (html) =>
    html.split(/<hr[^>]*class=["']?page-break["']?[^>]*>/gi);

  const parts = splitContent(applyTags(content));

  // 🔹 Get only image files from violation.files
  const imageFiles =
    violation.files?.filter((f) => f.file_type?.startsWith("image/")) || [];

  return (
    <Box
      id="styled-letter"
      sx={{
        backgroundColor: "#fff",
        color: "#222",
        maxWidth: 760,
        mx: "auto",
        fontFamily: `"Nunito Sans", "Helvetica Neue", Arial, sans-serif`,
        fontSize: "11pt",
        lineHeight: 1.6,
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <style>{`
        .page {
          page-break-after: always;
          padding: 32px 48px;
        }
        .page:last-child {
          page-break-after: auto;
        }
        .pdf-header {
          text-align: center;
          margin-bottom: 12px; /* 🔹 tightened */
          padding-bottom: 6px;
          border-bottom: 1px solid #ccc;
        }
        .pdf-header img {
          height: 100px; /* 🔹 increased */
          margin-bottom: 2px; /* 🔹 reduced */
        }
        .pdf-header .title {
          font-weight: bold;
          font-size: 14pt;
          margin: 0;
          line-height: 1.15; /* 🔹 tightened line-height */
        }
        .pdf-header .subtitle {
          font-size: 9.5pt;
          color: #555;
          margin: 0; /* 🔹 removed space */
          line-height: 1.1;
        }
        /* Paragraphs tighter */
        #styled-letter p {
          margin: 0 0 10px 0;
          font-size: 11pt;
        }
        /* Lists compact */
        #styled-letter ul {
          margin: 4px 0 10px 20px;
          padding: 0;
        }
        #styled-letter li {
          margin: 2px 0;
          line-height: 1.3;
        }
      `}</style>

      {parts.map((section, i) => (
        <div className="page" key={i}>
          {/* Header repeated on every page */}
<Box className="pdf-header">
  <img src={logo} alt="HOA Logo" />
  <Typography
    className="title"
    sx={{
      fontWeight: "bold",
      fontSize: "14pt",
      margin: "0 !important",   // 🔹 force reset
      padding: 0,
      lineHeight: 1.1,          // 🔹 keep compact
      display: "block",
    }}
  >
    {type || "Violation Notice"}
  </Typography>
  <Typography
  className="subtitle"
  sx={{
    fontSize: "9.5pt",
    color: "#555",
    margin: "0 !important",
    padding: 0,
    lineHeight: 1.0,
    display: "block",
  }}
>
  {deliveryMethod || "Delivered via US Mail and Email"}
</Typography>
</Box>


          {/* Section content */}
          <div
            style={{ fontSize: "inherit", lineHeight: "inherit" }}
            dangerouslySetInnerHTML={{ __html: section }}
          />

          {/* Evidence images only on last page */}
          {i === parts.length - 1 && imageFiles.length > 0 && (
            <Box sx={{ mt: 6, mb: 4 }}> {/* 🔹 more margin top, buffer from bottom */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  fontSize: "12pt",
                  borderBottom: "2px solid #ddd",
                  pb: 0.5,
                }}
              >
                Documentation:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                {imageFiles.map((file) => {
                  const fileUrl = file.file_url?.startsWith("http")
                  ? file.file_url
                  : `${API_BASE_URL}/violationsimages/${violation.id}/${encodeURIComponent(file.file_name)}`;

                  return (
                    <Box
                      key={file.id}
                      sx={{
                        p: 1.5,
                        border: "1px solid #ccc",
                        borderRadius: "5px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        backgroundColor: "#fafafa",
                        maxWidth: "70%",
                        textAlign: "center",
                      }}
                    >
                      <img
  src={fileUrl}
  alt={file.file_name || "Evidence image"}
  crossOrigin="anonymous"
  style={{
    maxWidth: "100%",
    maxHeight: "200px",
    borderRadius: "4px",
  }}
/>

                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </div>
      ))}
    </Box>
  );
};

export default ViolationLetterTemplate;
